export type MentionTrigger = "@" | "/";

export type ActiveMention = {
	start: number;
	query: string;
	trigger: MentionTrigger;
};

export type MentionToken =
	| { type: "text"; value: string }
	| { type: "workflow" | "skill"; value: string };

const ACTIVE_MENTION_RE = /(^|\s)([@/])([^@/\n]*)$/;

export function findActiveMention(
	text: string,
	caret?: number,
): ActiveMention | null {
	const beforeCaret = text.slice(0, caret ?? text.length);
	const match = ACTIVE_MENTION_RE.exec(beforeCaret);
	if (!match) return null;

	const boundary = match[1];
	const trigger = match[2] as MentionTrigger;
	const query = match[3];
	if (query.includes("]")) return null;

	return { start: match.index + boundary.length, query, trigger };
}

/** Replaces through captured caret/end. */
export function insertMention(
	text: string,
	mention: ActiveMention,
	name: string,
): string {
	const mentionEnd = mention.start + 1 + mention.query.length;
	return `${text.slice(0, mention.start)}${mention.trigger}[${name}] ${text.slice(mentionEnd)}`;
}

export const MENTION_TOKEN_RE = /([@/])\[([^\]\n]+)\]/g;

export function splitMentionTokens(text: string): MentionToken[] {
	const segments: MentionToken[] = [];
	const tokenPattern = new RegExp(
		MENTION_TOKEN_RE.source,
		MENTION_TOKEN_RE.flags,
	);
	let cursor = 0;

	for (const match of text.matchAll(tokenPattern)) {
		if (match.index > cursor) {
			segments.push({ type: "text", value: text.slice(cursor, match.index) });
		}
		segments.push({
			type: match[1] === "@" ? "workflow" : "skill",
			value: match[2],
		});
		cursor = match.index + match[0].length;
	}

	if (cursor < text.length) {
		segments.push({ type: "text", value: text.slice(cursor) });
	}

	return segments;
}

export type WorkflowInvocationSource = {
	fileId: string;
	driveId: string;
	name: string;
};

export type SkillInvocationSource = {
	slug: string;
	name: string;
};

export function invocationReferencesFor(
	text: string,
	workflows: readonly WorkflowInvocationSource[] | undefined,
	skills: readonly SkillInvocationSource[] | undefined,
): {
	workflowRefs: WorkflowInvocationSource[];
	skillRefs: Array<{ slug: string }>;
} {
	const workflowByName = new Map(
		(workflows ?? []).map((workflow) => [workflow.name, workflow]),
	);
	const skillByName = new Map(
		(skills ?? []).map((skill) => [skill.name, skill]),
	);
	const workflowRefs: WorkflowInvocationSource[] = [];
	const skillRefs: Array<{ slug: string }> = [];
	const workflowIds = new Set<string>();
	const skillSlugs = new Set<string>();
	for (const segment of splitMentionTokens(text)) {
		if (segment.type === "workflow") {
			const workflow = workflowByName.get(segment.value);
			if (!workflow) continue;
			const id = `${workflow.driveId}:${workflow.fileId}`;
			if (workflowIds.has(id)) continue;
			workflowIds.add(id);
			workflowRefs.push({
				fileId: workflow.fileId,
				driveId: workflow.driveId,
				name: workflow.name,
			});
			continue;
		}
		if (segment.type !== "skill") continue;
		const skill = skillByName.get(segment.value);
		if (!skill || skillSlugs.has(skill.slug)) continue;
		skillSlugs.add(skill.slug);
		skillRefs.push({ slug: skill.slug });
	}
	return { workflowRefs, skillRefs };
}
